import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft,
  FaUserCircle,
  FaSignOutAlt,
  FaServer,
  FaTrashAlt,
  FaUndo,
  FaSyncAlt,
  FaFilter,
  FaFileAlt,
  FaUser,
  FaBriefcase,
  FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const ENTITY_LABELS = {
  property_document: 'Document',
  property_access: 'Property access',
  job: 'Job',
  anchor_point: 'Anchor point',
  default: 'Item'
};

const ENTITY_ICONS = {
  property_document: FaFileAlt,
  property_access: FaUser,
  job: FaBriefcase,
  default: FaTrashAlt
};

const CloudStaffDeletedLogs = () => {
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadProfile();
    loadItems();
  }, [navigate]);

  useEffect(() => {
    loadItems();
  }, [typeFilter]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserName(`${data.firstName || data.first_name || ''} ${data.lastName || data.last_name || ''}`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (typeFilter && typeFilter !== 'all') params.set('entity_type', typeFilter);
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/deletion-log?${params}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      } else {
        toast.error('Failed to load deleted items');
        setItems([]);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load deleted items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/deletion-log/${id}/restore`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Restore failed');
      toast.success('Restored successfully');
      loadItems();
    } catch (e) {
      toast.error(e.message || 'Failed to restore');
    } finally {
      setRestoringId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const uniqueTypes = [...new Set(items.map((i) => i.entity_type))].sort();

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <Link to="/cloud-staff/dashboard">
            <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          </Link>
          <span className="cs-header-badge">Cloud Staff</span>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <Link to="/cloud-staff/dashboard" className="cs-icon-btn" title="Dashboard">
              <FaServer />
            </Link>
            <button
              type="button"
              className="cs-user-avatar"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="User menu"
            >
              <FaUserCircle />
            </button>
            {showUserMenu && (
              <div className="cs-user-menu">
                <div className="cs-user-menu-header">
                  <FaUserCircle className="cs-menu-avatar" />
                  <span className="cs-menu-name">{userName || 'User'}</span>
                </div>
                <button type="button" className="cs-menu-item" onClick={handleLogout}>
                  <FaSignOutAlt /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="deleted-logs" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid #21262d'
            }}
          >
            <Link
              to="/cloud-staff/dashboard"
              style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid #30363d',
                color: '#8b949e',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
            >
              <FaArrowLeft />
            </Link>
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}>
                Deleted Logs
              </h1>
              <p style={{ fontSize: '0.85rem', color: '#8b949e', margin: 0 }}>
                Everything that has been deleted in the app. View details and undo (restore) when needed.
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1rem',
              flexWrap: 'wrap'
            }}
          >
            <button
              type="button"
              className="cs-primary-btn"
              onClick={loadItems}
              disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FaSyncAlt style={loading ? { animation: 'cs-spin 0.8s linear infinite' } : {}} /> Refresh
            </button>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#8b949e', fontSize: '0.9rem' }}>
              <FaFilter />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{
                  padding: '0.4rem 0.6rem',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '6px',
                  color: '#e1e4e8',
                  fontSize: '0.85rem'
                }}
              >
                <option value="all">All types</option>
                {uniqueTypes.map((t) => (
                  <option key={t} value={t}>
                    {ENTITY_LABELS[t] || t}
                  </option>
                ))}
              </select>
            </span>
            <span style={{ fontSize: '0.85rem', color: '#8b949e' }}>{items.length} deleted items</span>
          </div>

          {loading ? (
            <div className="cs-loading">
              <div className="cs-spinner" />
              <p>Loading deleted items...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="cs-empty-state">
              <FaTrashAlt style={{ fontSize: '3rem', color: '#484f58', marginBottom: '0.5rem' }} />
              <p>No deleted items</p>
              <p style={{ fontSize: '0.85rem', color: '#8b949e', marginTop: '0.5rem' }}>
                When something is deleted (documents, property access, jobs, etc.), it will appear here so you can review or undo it.
              </p>
            </div>
          ) : (
            <div className="cs-activity-table" style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Deleted at</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Deleted by</th>
                    <th>Property</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const Icon = ENTITY_ICONS[item.entity_type] || ENTITY_ICONS.default;
                    const typeLabel = ENTITY_LABELS[item.entity_type] || item.entity_type;
                    return (
                      <tr key={item.id}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{formatDate(item.deleted_at)}</td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            <Icon style={{ color: '#8b949e', fontSize: '0.9rem' }} />
                            {typeLabel}
                          </span>
                        </td>
                        <td style={{ maxWidth: '320px' }} title={item.entity_label}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                            {item.entity_label}
                          </span>
                        </td>
                        <td>{item.deleted_by_name || '—'}</td>
                        <td>{item.property_name || '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="cs-primary-btn"
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
                            onClick={() => handleRestore(item.id)}
                            disabled={restoringId === item.id}
                            title="Undo – restore this item"
                          >
                            {restoringId === item.id ? '…' : <><FaUndo /> Undo</>}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CloudStaffDeletedLogs;
